import { useState, useRef, useEffect } from "react";
import axios from "axios";

const API = "/api";

const ChatBot = () => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Olá! Sou a Dalpha Prime, a tua assistente pessoal com acesso à internet. Como posso ajudar-te hoje?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const playAudio = (base64Audio) => {
    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      
      // Create audio from base64
      const audioBlob = new Blob(
        [Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        audioRef.current = null;
      };
      
      audio.onerror = (e) => {
        console.error("Audio playback error:", e);
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        audioRef.current = null;
      };
      
      setIsPlaying(true);
      audio.play().catch(e => {
        console.error("Audio play failed:", e);
        setIsPlaying(false);
      });
    } catch (e) {
      console.error("Error creating audio:", e);
      setIsPlaying(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      // Use voice endpoint if voice is enabled
      const endpoint = voiceEnabled ? `${API}/chat/voice` : `${API}/chat`;
      
      const response = await axios.post(endpoint, {
        message: userMessage,
        chat_id: "web_user_1"
      }, { timeout: 60000 });

      const assistantMessage = response.data.response || "Desculpa, não consegui processar a mensagem.";
      
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: assistantMessage,
        hasAudio: !!response.data.audio
      }]);
      
      // Play audio if available and voice is enabled
      if (voiceEnabled && response.data.audio) {
        playAudio(response.data.audio);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Erro ao conectar. Tenta novamente." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleVoice = () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
    }
    setVoiceEnabled(!voiceEnabled);
  };

  return (
    <div data-testid="chatbot-container" className="chat-container">
      <div className="chat-header">
        <div className="chat-header-icon">💫</div>
        <div className="chat-header-info">
          <h3>Dalpha Prime</h3>
          <span className="chat-status">Online</span>
        </div>
        <button 
          onClick={toggleVoice}
          className={`voice-toggle-btn ${voiceEnabled ? 'active' : ''} ${isPlaying ? 'playing' : ''}`}
          title={voiceEnabled ? "Desligar voz" : "Ligar voz"}
          data-testid="voice-toggle-btn"
        >
          {isPlaying ? '🔊' : (voiceEnabled ? '🔈' : '🔇')}
        </button>
      </div>

      <div className="chat-messages" data-testid="chat-messages">
        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`chat-message ${msg.role}`}
            data-testid={`chat-message-${idx}`}
          >
            {msg.role === "assistant" && <span className="chat-avatar">💫</span>}
            <div className="chat-bubble">
              {msg.content}
            </div>
            {msg.role === "user" && <span className="chat-avatar user">👤</span>}
          </div>
        ))}
        {loading && (
          <div className="chat-message assistant">
            <span className="chat-avatar">💫</span>
            <div className="chat-bubble typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="chat-input-form">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escreve uma mensagem..."
          disabled={loading}
          data-testid="chat-input"
          className="chat-input"
        />
        <button 
          type="submit" 
          disabled={loading || !input.trim()}
          data-testid="chat-send-btn"
          className="chat-send-btn"
        >
          {loading ? "..." : "➤"}
        </button>
      </form>
    </div>
  );
};

export default ChatBot;
